'use client';

import { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

interface YandexMetrikaState {
  counterId: number | null;
  isReady: boolean;
}

const initialState: YandexMetrikaState = {
  counterId: null,
  isReady: false,
};

let cachedState: YandexMetrikaState = initialState;
let initPromise: Promise<void> | null = null;
const subscribers = new Set<(s: YandexMetrikaState) => void>();

function publish(next: YandexMetrikaState) {
  cachedState = next;
  subscribers.forEach((cb) => cb(next));
}

function ensureInited() {
  if (initPromise) return initPromise;

  const baseUrl = getApiBaseUrl();

  initPromise = fetch(`${baseUrl}/api/public/config`)
    .then((res) => res.json())
    .then((data: { yandexMetrikaCounterId: number | null }) => {
      const counterId = data.yandexMetrikaCounterId;
      if (!counterId) {
        publish({ counterId: null, isReady: true });
        return;
      }

      if (typeof window.ym === 'undefined') {
        (window as any).ym = function (...args: any[]) {
          ((window as any).ym.a = (window as any).ym.a || []).push(args);
        };
        (window as any).ym.l = Date.now();
      }

      publish({ counterId, isReady: true });

      const script = document.createElement('script');
      script.src = `https://mc.yandex.ru/metrika/tag.js?id=${counterId}`;
      script.async = true;
      script.onload = () => {
        // clickmap выключена: карта кликов слала ~2 млн запросов /clmap/ в сутки
        // (~35 на сессию) поверх мобильного 4G — цена сильно выше пользы от тепловой карты.
        ym(counterId, 'init', {
          clickmap: false,
          trackLinks: true,
          accurateTrackBounce: true,
          ecommerce: 'dataLayer',
        } as any);
      };
      document.head.appendChild(script);
    })
    .catch(() => {
      publish({ counterId: null, isReady: true });
    });

  return initPromise;
}

export function useYandexMetrika(): YandexMetrikaState {
  const [state, setState] = useState<YandexMetrikaState>(cachedState);

  useEffect(() => {
    subscribers.add(setState);
    setState(cachedState);
    // Defer off the critical boot path — Metrika loads a third-party script
    // (tag.js) and isn't needed for first paint; running it during idle keeps
    // it from competing with hydration on the main thread.
    const hasRIC = typeof window !== 'undefined' && 'requestIdleCallback' in window;
    const id = hasRIC
      ? window.requestIdleCallback(() => { void ensureInited(); }, { timeout: 3000 })
      : window.setTimeout(() => { void ensureInited(); }, 1500);
    return () => {
      subscribers.delete(setState);
      if (hasRIC) window.cancelIdleCallback(id as number);
      else window.clearTimeout(id as number);
    };
  }, []);

  return state;
}
